import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CoachLandingHeader from "./CoachLandingHeader";
import CoachLandingHero from "./CoachLandingHero";
import CoachLandingDiscovery from "./CoachLandingDiscovery";
import CoachLandingSegmentos from "./CoachLandingSegmentos";
import CoachLandingCierre from "./CoachLandingCierre";
import CoachLandingSolution from "./CoachLandingSolution";
import CoachProductTour from "./CoachProductTour";
import CoachLandingComparison from "./CoachLandingComparison";
import CoachLandingPricingCompare from "./CoachLandingPricingCompare";
import CoachLandingSteps from "./CoachLandingSteps";
import CoachLandingEarnings from "./CoachLandingEarnings";
import CoachLandingTestimonials from "./CoachLandingTestimonials";
import CoachLandingFAQ from "./CoachLandingFAQ";
import WaitlistCoach from "./WaitlistCoach";
import Footer from "../Footer";

export default function CoachLandingPage() {
  const location = useLocation();

  // Al llegar con un hash (p. ej. desde el blog: /coaches#earnings), la SPA aún
  // no ha montado las secciones cuando el navegador procesa el hash -> hacemos
  // el scroll nosotros una vez montado. Dos intentos por si cargan imágenes.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash;
    const scrollToTarget = () => {
      const el = document.querySelector(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    const t1 = setTimeout(scrollToTarget, 350);
    const t2 = setTimeout(scrollToTarget, 1000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.hash]);

  // Reveal al hacer scroll para todas las secciones de la landing. Añadimos la
  // clase por JS (no en el JSX) para que, si el JS falla, el contenido se vea
  // igual. El tour tiene su propio observer, así que aquí lo excluimos.
  useEffect(() => {
    const selectors = [
      ".solution-header", ".segmentos-header", ".segmento-card", ".compare-header", ".steps-header",
      ".earnings-header", ".testimonials-header", ".faq-header",
      ".feature-card", ".step-card", ".pc-card",
      ".earnings-card", ".testimonial-card", ".faq-item", ".compare-row",
    ];
    const els = Array.from(document.querySelectorAll(selectors.join(",")));
    if (!els.length) return;

    els.forEach((el) => el.classList.add("reveal-on-scroll"));
    // Stagger: los elementos repetidos (cards de una misma rejilla) entran en
    // cascada según su posición entre hermanos que también se animan.
    els.forEach((el) => {
      const group = Array.from(el.parentElement ? el.parentElement.children : []).filter(
        (c) => c.classList.contains("reveal-on-scroll")
      );
      const i = group.indexOf(el);
      if (i > 0) el.style.transitionDelay = `${Math.min(i * 0.07, 0.35)}s`;
    });

    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -50px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Software para Entrenadores Personales Gratis | Wayo Coach</title>
        <meta
          name="description"
          content="Clientes, rutinas, dietas y seguimiento en un solo sitio. Para entrenadores personales, coaches online y nutricionistas. Gratis y sin límite de clientes."
        />
        <meta
          name="keywords"
          content="software gratis para entrenadores, app para entrenadores personales, software para nutricionistas, plataforma para coaches fitness, gestión de clientes de entrenamiento, planes de nutrición con IA, alternativa a Harbiz gratis, software para preparadores físicos, app para boxes de crossfit"
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://wayoai.app/coaches" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wayo" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:url" content="https://wayoai.app/coaches" />
        <meta property="og:image" content="https://wayoai.app/assets/img/og-coaches.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Panel de Wayo Coach: software gratis para entrenadores" />
        <meta name="twitter:image" content="https://wayoai.app/assets/img/og-coaches.jpg" />
        <meta property="og:title" content="Wayo Coach | Software gratis para entrenadores y coaches" />
        <meta
          property="og:description"
          content="Lo que otras plataformas cobran de 14 a 199 €/mes, en Wayo gratis: clientes, rutinas, nutrición con IA y seguimiento. Sin permanencia y sin límite de clientes."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Wayo Coach | Software gratis para entrenadores" />
        <meta
          name="twitter:description"
          content="Gestiona clientes, rutinas y nutrición gratis. La alternativa a Harbiz sin cuotas."
        />

        {/* Structured data: producto de software gratuito */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Wayo Coach",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, iOS, Android",
            url: "https://wayoai.app/coaches",
            description:
              "Software todo-en-uno para entrenadores personales, boxes, centros, nutricionistas y fisioterapeutas: gestión de clientes, planes de entrenamiento y nutrición con IA, calendario, chat y control de cobros. Gratis para el profesional.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "EUR"
            }
          })}
        </script>
      </Helmet>

      <CoachLandingHeader />
      <CoachLandingHero />
      {/* El tour del producto va justo tras el hero: enseñar el panel por dentro
          es el argumento más fuerte y antes quedaba enterrado a mitad de página. */}
      <CoachProductTour />
      {/* 1-oct-2026, siguiendo a Harbiz: primero para quién es, luego qué hace,
          luego cuánto cuesta frente a la alternativa, y la confianza al final.
          La lista «Todo esto es gratis» repetía las funciones y se quitó. */}
      <CoachLandingSegmentos />
      <CoachLandingSolution />
      <CoachLandingComparison />
      <CoachLandingPricingCompare />
      <CoachLandingEarnings />
      <CoachLandingSteps />
      <CoachLandingDiscovery />
      <CoachLandingTestimonials />
      <CoachLandingFAQ />
      <CoachLandingCierre />
      <WaitlistCoach />
      <Footer />
    </>
  );
}
